import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Alert, Chip } from '@mui/material';
import { AppDispatch } from '../../store/store';
import useDebounce from '../../components/useDebounce';
import {
  getGroupAdminReports,
  type GroupAdminReport,
  type Pagination,
  type StudyType,
  type Attention,
} from '../../rtk-apis/groupAdmin/groupAdminApi';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import GroupAdminLayout from './GroupAdminLayout';
import {
  CommandBar,
  Segmented,
  FilterMenu,
  DateField,
  ClinicFilter,
  HeadBand,
  RowCard,
  Identity,
  Cell,
  StatusPill,
  ListState,
  ListPager,
  formatDate,
  formatName,
  statusColor,
} from './GroupAdminShared';

/* Reports across the account's clinics, on the staff Reports list surface.
   Read-only: a row opens the report view. */

const COLUMNS = 'minmax(250px, 2fr) minmax(170px, 1.3fr) minmax(110px, 0.8fr) minmax(100px, 0.7fr) minmax(170px, 1.2fr) minmax(116px, 0.8fr)';
const MIN_WIDTH = 1050;
const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Draft', label: 'Draft', className: 'csl-status--draft' },
  { value: 'Preliminary', label: 'Preliminary', className: 'csl-status--prelim' },
  { value: 'Finalized', label: 'Finalized', className: 'csl-status--final' },
];
const EMPTY_FILTERS = {
  clinicId: '',
  status: '',
  studyType: '' as StudyType | '',
  attention: '' as Attention | '',
  dateFrom: '',
  dateTo: '',
};
const ATTENTION_LABELS: Record<Attention, string> = {
  overdue: 'Overdue',
  unassigned: 'Unassigned',
  open: 'All open',
};

const GroupAdminReports: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [searchParams] = useSearchParams();
  // The dashboard's "Needs attention → View all" opens this list pre-filtered.
  const [filters, setFilters] = useState<typeof EMPTY_FILTERS>(() => {
    const attention = searchParams.get('attention') as Attention | null;
    return {
      ...EMPTY_FILTERS,
      clinicId: searchParams.get('clinicId') || '',
      attention: attention && attention in ATTENTION_LABELS ? attention : '',
    };
  });
  const [moreOpen, setMoreOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [rows, setRows] = useState<GroupAdminReport[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ total: 0, page: 1, limit: 25, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (patch: Partial<typeof EMPTY_FILTERS>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await dispatch(getGroupAdminReports({ ...filters, search: debouncedSearch.trim(), page, limit: perPage })).unwrap();
      setRows(res.data);
      setPagination(res.pagination);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Could not load reports');
    } finally {
      setLoading(false);
    }
  }, [dispatch, filters, debouncedSearch, page, perPage]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => setPage(1), [debouncedSearch]);

  const isFiltering = !!(search || Object.values(filters).some(Boolean));

  return (
    <GroupAdminLayout>
      <div className="csl crl">
        <CommandBar
          title="Reports"
          count={pagination.total}
          noun={pagination.total === 1 ? 'report' : 'reports'}
          search={{ value: search, placeholder: 'Search patient, ID or report no…', onChange: setSearch }}
        />

        <div className="csl-filters">
          {filters.attention ? (
            <Chip
              label={`${ATTENTION_LABELS[filters.attention]} · oldest first`}
              onDelete={() => set({ attention: '' })}
              sx={{ color: T.amber, border: `1px solid ${T.amber}55`, background: `${T.amber}1a` }}
            />
          ) : (
            <Segmented value={filters.status} options={STATUS_OPTIONS} onChange={(status) => set({ status })} />
          )}
          <ClinicFilter value={filters.clinicId} onChange={(clinicId) => set({ clinicId })} />
          <FilterMenu
            value={filters.studyType}
            onChange={(studyType) => set({ studyType: studyType as StudyType | '' })}
            allLabel="Echo and stress"
            heading="Filter by type"
            options={[
              { value: 'echo', label: 'Echo' },
              { value: 'stressEcho', label: 'Stress echo' },
            ]}
          />
          <button className={`csl-more ${moreOpen ? 'is-open' : ''}`} onClick={() => setMoreOpen((o) => !o)}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 6h16M7 12h10M10 18h4" />
            </svg>
            More filters
          </button>
          {isFiltering && (
            <button
              className="csl-clear"
              onClick={() => {
                setSearch('');
                setFilters(EMPTY_FILTERS);
                setPage(1);
              }}
            >
              Clear all
            </button>
          )}
        </div>

        {moreOpen && (
          <div className="csl-more-panel">
            <DateField label="Created from" value={filters.dateFrom} onChange={(dateFrom) => set({ dateFrom })} />
            <DateField label="Created to" value={filters.dateTo} onChange={(dateTo) => set({ dateTo })} />
          </div>
        )}

        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {rows.length > 0 && (
          <HeadBand columns={COLUMNS} minWidth={MIN_WIDTH} labels={['Patient', 'Clinic', 'Study date', 'Type', 'Cardiologist', 'Status']} />
        )}

        <div className="csl-list">
          <ListState
            loading={loading}
            empty={!rows.length}
            icon="📄"
            title="No reports found"
            hint={isFiltering ? 'Try clearing filters' : 'Reports from your clinics will appear here'}
          />
          {rows.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 'fit-content', flexShrink: 0 }}>
              {rows.map((r) => (
                <RowCard key={r._id} columns={COLUMNS} minWidth={MIN_WIDTH} stripe={statusColor(r.status)} onClick={() => navigate(`/group-admin/reports/${r._id}`)}>
                  <Identity name={formatName(r.patientName)} initialsFrom={r.patientName} sub={r.patientId ? `MRN ${r.patientId}` : '—'} />
                  <Cell>{r.clinicName || '—'}</Cell>
                  <Cell sub={r.reportNumber ? `No. ${r.reportNumber}` : undefined}>{formatDate(r.studyDate)}</Cell>
                  <Cell>
                    <span className="csl-modality">{r.studyType === 'stressEcho' ? 'Stress' : 'Echo'}</span>
                  </Cell>
                  <Cell sub={r.sonographer ? `Sono: ${r.sonographer}` : undefined}>{r.reportAssignedTo || 'Unassigned'}</Cell>
                  <Box>
                    <StatusPill status={r.status} />
                  </Box>
                </RowCard>
              ))}
            </Box>
          )}
        </div>

        <ListPager
          pagination={pagination}
          onPage={setPage}
          onPerPage={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      </div>
    </GroupAdminLayout>
  );
};

export default GroupAdminReports;
